<template>
    <div class="py-6">
        <!-- Summary Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <!-- Total Deposits -->
            <div
                class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
                <div class="flex items-center justify-between">
                    <div class="flex-1">
                        <div
                            class="text-xs font-medium text-gray-500 uppercase mb-1"
                        >
                            Total Deposits
                        </div>
                        <div class="text-2xl font-bold text-green-600">
                            GMD {{ formatPrice(stats.totalDeposits) }}
                        </div>
                        <div class="text-xs text-gray-500 mt-1">
                            {{ formatNumber(stats.depositsCount) }} transactions
                        </div>
                    </div>
                    <div
                        class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center"
                    >
                        <i
                            class="bx bx-down-arrow-circle text-2xl text-green-600"
                        ></i>
                    </div>
                </div>
            </div>

            <!-- Total Sales -->
            <div
                class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
                <div class="flex items-center justify-between">
                    <div class="flex-1">
                        <div
                            class="text-xs font-medium text-gray-500 uppercase mb-1"
                        >
                            Total Sales
                        </div>
                        <div class="text-2xl font-bold text-blue-600">
                            GMD {{ formatPrice(stats.totalSales) }}
                        </div>
                        <div class="text-xs text-gray-500 mt-1">
                            {{ formatNumber(stats.salesCount) }} transactions
                        </div>
                    </div>
                    <div
                        class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"
                    >
                        <i
                            class="bx bx-up-arrow-circle text-2xl text-blue-600"
                        ></i>
                    </div>
                </div>
            </div>

            <!-- Agent Commission -->
            <div
                class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
                <div class="flex items-center justify-between">
                    <div class="flex-1">
                        <div
                            class="text-xs font-medium text-gray-500 uppercase mb-1"
                        >
                            Agent Commission
                        </div>
                        <div class="text-2xl font-bold text-purple-600">
                            GMD {{ formatPrice(stats.totalCommission) }}
                        </div>
                    </div>
                    <div
                        class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center"
                    >
                        <i
                            class="bx bx-dollar-circle text-2xl text-purple-600"
                        ></i>
                    </div>
                </div>
            </div>

            <!-- Chapman Commission -->
            <div
                class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
                <div class="flex items-center justify-between">
                    <div class="flex-1">
                        <div
                            class="text-xs font-medium text-gray-500 uppercase mb-1"
                        >
                            Chapman Commission
                        </div>
                        <div class="text-2xl font-bold text-orange-600">
                            GMD {{ formatPrice(stats.chapmanCommission) }}
                        </div>
                    </div>
                    <div
                        class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center"
                    >
                        <i class="bx bx-receipt text-2xl text-orange-600"></i>
                    </div>
                </div>
            </div>

            <!-- Deposit Reversals -->
            <div
                class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
                <div class="flex items-center justify-between">
                    <div class="flex-1">
                        <div
                            class="text-xs font-medium text-gray-500 uppercase mb-1"
                        >
                            Deposit Reversals
                        </div>
                        <div class="text-2xl font-bold text-red-600">
                            GMD {{ formatPrice(stats.totalDepositReversals) }}
                        </div>
                        <div class="text-xs text-gray-500 mt-1">
                            {{ formatNumber(stats.depositReversalsCount) }}
                            reversals
                        </div>
                    </div>
                    <div
                        class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center"
                    >
                        <i class="bx bx-revision text-2xl text-red-600"></i>
                    </div>
                </div>
            </div>

            <!-- Net Activity -->
            <div
                class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
                <div class="flex items-center justify-between">
                    <div class="flex-1">
                        <div
                            class="text-xs font-medium text-gray-500 uppercase mb-1"
                        >
                            Net Activity
                        </div>
                        <div
                            class="text-2xl font-bold"
                            :class="
                                netActivity >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                            "
                        >
                            {{ netActivity >= 0 ? '+' : '' }}GMD
                            {{ formatPrice(netActivity) }}
                        </div>
                        <div class="text-xs text-gray-500 mt-1">
                            Deposits - Sales - Chapman Commission
                        </div>
                    </div>
                    <div
                        class="w-12 h-12 rounded-full flex items-center justify-center"
                        :class="
                            netActivity >= 0 ? 'bg-green-100' : 'bg-red-100'
                        "
                    >
                        <i
                            :class="
                                netActivity >= 0
                                    ? 'bx bx-trending-up text-green-600'
                                    : 'bx bx-trending-down text-red-600'
                            "
                            class="text-2xl"
                        ></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- Date Range Filter -->
        <div
            class="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm"
        >
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1"
                        >From Date</label
                    >
                    <input
                        type="date"
                        v-model="filters.dateFrom"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                </div>
                <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1"
                        >To Date</label
                    >
                    <input
                        type="date"
                        v-model="filters.dateTo"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                </div>
                <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1"
                        >Transaction Type</label
                    >
                    <select
                        v-model="filters.transactionType"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    >
                        <option value="">All Types</option>
                        <option value="deposit">Deposit</option>
                        <option value="sale">Sale</option>
                        <option value="reversal">Reversal</option>
                    </select>
                </div>
                <div class="flex items-end">
                    <button
                        @click="loadStats"
                        :disabled="true"
                        class="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        <i class="bx bx-search mr-1"></i> Apply
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
            <div
                class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"
            ></div>
            <span class="ml-2 text-gray-600">Loading reports...</span>
        </div>

        <!-- Reports Table -->
        <div
            v-else
            class="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
        >
            <div class="px-4 py-3 border-b border-gray-200">
                <h3 class="text-sm font-medium text-gray-700">
                    Transaction History
                </h3>
            </div>

            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                Date
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                Type
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                Amount
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                Commission
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                Status
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr
                            v-for="(transaction, index) in filteredTransactions"
                            :key="index"
                        >
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                            >
                                {{
                                    transaction.date
                                        | moment('MMM DD, YYYY HH:mm')
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                            >
                                {{ transaction.type }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                            >
                                GMD {{ formatPrice(transaction.amount) }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-green-600"
                            >
                                GMD {{ formatPrice(transaction.commission) }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    :class="getStatusClass(transaction.status)"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                >
                                    {{ transaction.status }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Empty State -->
                <div
                    v-if="filteredTransactions.length === 0"
                    class="text-center py-12"
                >
                    <i class="bx bx-file text-4xl text-gray-400"></i>
                    <p class="mt-2 text-gray-500">No transactions found</p>
                    <p class="text-sm text-gray-400">
                        Try adjusting your date range
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    props: ['data'],

    data: () => ({
        loading: false,
        filters: {
            dateFrom: '',
            dateTo: '',
            transactionType: '',
        },
        stats: {
            totalDeposits: 0,
            depositsCount: 0,
            totalSales: 0,
            salesCount: 0,
            totalCommission: 0,
            chapmanCommission: 0,
            totalDepositReversals: 0,
            depositReversalsCount: 0,
        },
        transactions: [],
    }),

    computed: {
        netActivity() {
            console.log(
                'this.stats.totalDepositReversals ',
                this.stats.totalDepositReversals
            );
            return (
                this.stats.totalDeposits +
                this.stats.totalDepositReversals -
                this.stats.totalSales -
                this.stats.chapmanCommission
            );
        },

        filteredTransactions() {
            if (!this.filters.transactionType) {
                return this.transactions;
            }

            return this.transactions.filter(transaction => {
                const transactionType = transaction.type?.toLowerCase() || '';
                return transactionType.includes(
                    this.filters.transactionType.toLowerCase()
                );
            });
        },
    },

    created() {
        this.setDefaultDates();
        this.loadStats();
    },

    methods: {
        setDefaultDates() {
            const today = new Date();
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

            this.filters.dateFrom = firstDay.toISOString().split('T')[0];
            this.filters.dateTo = today.toISOString().split('T')[0];
        },

        async loadStats() {
            if (!this.data || !this.data.id) return;

            this.loading = true;

            try {
                const params = new URLSearchParams();
                params.append('agent_id', this.data.id);
                if (this.filters.dateFrom)
                    params.append('date_from', this.filters.dateFrom);
                if (this.filters.dateTo)
                    params.append('date_to', this.filters.dateTo);

                const { data } = await axios.get(
                    `/agent/${this.data.id}/reports?${params.toString()}`
                );

                this.stats = {
                    totalDeposits: data.total_deposits || 0,
                    depositsCount: data.deposits_count || 0,
                    totalSales: data.total_sales || 0,
                    salesCount: data.sales_count || 0,
                    totalCommission: data.total_commission || 0,
                    chapmanCommission: data.chapman_commission || 0,
                    totalDepositReversals: data.total_deposit_reversals || 0,
                    depositReversalsCount: data.deposit_reversals_count || 0,
                };
                this.transactions = data.transactions || [];

                this.loading = false;
            } catch (e) {
                console.error('Error loading agent reports:', e);
                this.loading = false;
            }
        },

        async exportReport() {
            // Implement export functionality
            console.log('Exporting report...');
        },

        formatPrice(value) {
            if (!value && value !== 0) return '0.00';
            let val = (parseFloat(value) / 1).toFixed(2).replace(',', '.');
            return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },

        formatNumber(value) {
            if (!value && value !== 0) return '0';
            return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },

        getStatusClass(status) {
            const statusMap = {
                completed: 'bg-green-100 text-green-800',
                pending: 'bg-yellow-100 text-yellow-800',
                failed: 'bg-red-100 text-red-800',
            };
            return (
                statusMap[status?.toLowerCase()] || 'bg-gray-100 text-gray-800'
            );
        },
    },
};
</script>
